import React, { useEffect, useState } from "react";
import { fetchWithAuth } from "../Context/UserContext";
import { useNavigate } from "react-router-dom";
import API_URL from "../config";
import { Pencil, X } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const UserReviews = ({ userId, isOwner }) => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedReview, setExpandedReview] = useState(null);

  useEffect(() => {
    const fetchUserReviews = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/review/user/${userId}/reviews?page=${currentPage}`
        );
        const data = await response.json();

        if (response.ok) {
          setReviews(data.reviews || []);
          setTotalPages(data.total_pages || 1);
        } else {
          console.error("Error al obtener reviews:", data.error);
        }
      } catch (error) {
        console.error("Error en la solicitud de reviews:", error);
      }
    };

    fetchUserReviews();
  }, [userId, currentPage]);

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar esta reseña?")) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/review/delete/${reviewId}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success("Reseña eliminada exitosamente.");
        setReviews((prevReviews) =>
          prevReviews.filter((review) => review.id_review !== reviewId)
        );
      } else {
        toast.error(data.error || "No se pudo eliminar la reseña.");
      }
    } catch (error) {
      console.error("Error eliminando la reseña:", error);
      toast.error("Error al eliminar la reseña.");
    }
  };

  const scoreColors = ["#d62246", "#f97316", "#eab308", "#4bbf73", "#2A9D8F"];

  return (
    <div className="space-y-4">
      {reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id_review}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">Empresa:</strong>{" "}
                    <a
                      href={`/company-profile/${review.id_company}/${review.nombre_empresa}`}
                      className="text-primary hover:underline"
                    >
                      {review.nombre_empresa}
                    </a>
                  </p>
                  {isOwner && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          navigate("/reseñas", {
                            state: { reviewId: review.id_review },
                          })
                        }
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Editar reseña"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteReview(review.id_review)}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Eliminar reseña"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-2 flex items-center justify-between">
                  <p className="flex items-center text-sm">
                    <span className="flex">
                      {Array(5)
                        .fill(0)
                        .map((_, index) => {
                          const roundedScore = Math.round(review.puntaje_global * 2) / 2; // Redondeo al 0.5 más cercano
                          const isFull = index + 1 <= Math.floor(roundedScore); // Estrella completamente llena
                          const isHalf = index + 1 === Math.ceil(roundedScore) && !isFull; // Media estrella

                          const colorIndex =
                            Math.min(Math.ceil(review.puntaje_global), scoreColors.length) - 1;

                          return (
                            <span
                              key={index}
                              className="mr-0.5 inline-block h-5 w-5 rounded-full p-0.5 text-center text-xs"
                              style={{
                                background: isFull
                                  ? scoreColors[colorIndex]
                                  : isHalf
                                  ? `linear-gradient(90deg, ${scoreColors[colorIndex]} 50%, #ccc 50%)`
                                  : "#ccc",
                                color: "white",
                              }}
                            >
                              ★
                            </span>
                          );
                        })}
                    </span>
                    <span className="ml-1 text-muted-foreground">: {review.puntaje_global.toFixed(1)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{review.created_at}</p>
                </div>

                <p className="mt-2 font-semibold text-foreground">{review.titulo}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {expandedReview === review.id_review
                    ? review.comentario
                    : `${review.comentario.slice(0, 500)}...`}
                  {review.comentario.length > 500 && expandedReview !== review.id_review && (
                    <button
                      onClick={() => setExpandedReview(review.id_review)}
                      className="ml-1 text-primary hover:underline"
                    >
                      Ver más
                    </button>
                  )}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  <strong className="text-foreground">Fecha:</strong> {review.fecha_experiencia}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Este usuario no ha escrito reseñas aún.</p>
      )}

      {/* Paginación */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Atrás
        </Button>

        {currentPage > 2 && (
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)}>
            1
          </Button>
        )}

        {currentPage > 3 && <span className="text-muted-foreground">...</span>}

        {currentPage > 1 && (
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage - 1)}>
            {currentPage - 1}
          </Button>
        )}

        <Button size="sm">{currentPage}</Button>

        {currentPage < totalPages && (
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage + 1)}>
            {currentPage + 1}
          </Button>
        )}

        {currentPage < totalPages - 2 && <span className="text-muted-foreground">...</span>}

        {currentPage < totalPages - 1 && (
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)}>
            {totalPages}
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
};

export default UserReviews;
